import type { Locale } from "@/lib/locale";
// Competitor comparison matrix — used by the four /comparatif/* pages.
//
// Stance:
//   • Facts only, sourced from each competitor's own public marketing
//     pages (kept in `sourceUrl` so it can be re-verified).
//   • Never disparage. Each "summary" leads with "Choose <competitor> if
//     ..." before stating where delapresse fits.
//   • Always link to the competitor's homepage in the page header.
//   • Pricing notes are estimates with a footnote disclaimer at render
//     time — read the disclaimer string in the page component.

export type CompetitorSlug =
  | "chatgpt-seul"
  | "zapier-make"
  | "agence-redaction";

export interface BilingualText {
  fr: string;
  en: string;
  /** Machine-translated from `en` by scripts/i18n/translate.mjs. */
  es?: string;
  ar?: string;
}

/** Verdict that a feature is offered by the column owner. */
export type CellVerdict =
  | { kind: "yes"; note?: BilingualText }
  | { kind: "partial"; note?: BilingualText }
  | { kind: "no" }
  | { kind: "text"; value: BilingualText };

export interface ComparisonRow {
  id: string;
  label: BilingualText;
  delapresse: CellVerdict;
  competitor: CellVerdict;
}

export interface UseCaseFitRow {
  id: string;
  audience: BilingualText;
  delapresseVerdict: BilingualText;
  competitorVerdict: BilingualText;
}

export interface MigrationStep {
  step: number;
  title: BilingualText;
  body: BilingualText;
}

export interface CompetitorDefinition {
  slug: CompetitorSlug;
  /** Display label for the column header. Do not pluralise. */
  competitorName: string;
  /** Homepage we always link to. */
  homepageUrl: string;
  /** Heading: "delapresse vs ..." */
  heroTitle: BilingualText;
  /** Lead paragraph under the hero. */
  heroLead: BilingualText;

  /** "Choose X if ... Choose delapresse if ..." — body of the honest box. */
  summaryChooseCompetitor: BilingualText;
  summaryChooseDelapresse: BilingualText;

  /** Feature-by-feature comparison, 15–20 rows. */
  rows: ComparisonRow[];

  /** Audience verdict table. */
  useCaseFit: UseCaseFitRow[];

  /** Pricing estimate. Always render the disclaimer string below it. */
  pricingDelapresse: BilingualText;
  pricingCompetitor: BilingualText;
  pricingNote: BilingualText;

  /** Three-step migration mini-section. */
  migration: MigrationStep[];

  /** Source URLs consulted; surfaced under the table for transparency. */
  sourceUrls: string[];
}

const T = (fr: string, en: string, es?: string, ar?: string): BilingualText => ({ fr, en, es, ar });
const Y = (note?: BilingualText): CellVerdict => ({ kind: "yes", note });
const N: CellVerdict = { kind: "no" };
const P = (note?: BilingualText): CellVerdict => ({ kind: "partial", note });
const TXT = (fr: string, en: string): CellVerdict => ({ kind: "text", value: T(fr, en) });

// ─── ChatGPT alone ──────────────────────────────────────────────────────────

const CHATGPT: CompetitorDefinition = {
  slug: "chatgpt-seul",
  competitorName: "ChatGPT seul",
  homepageUrl: "https://chat.openai.com/",
  heroTitle: T(
    "delapresse vs ChatGPT seul : ce que les médias choisissent en 2026",
    "delapresse vs ChatGPT alone: what newsrooms pick in 2026",
  ),
  heroLead: T(
    "ChatGPT est excellent pour écrire un article à la demande. delapresse est un pipeline qui orchestre les sources, la déduplication, l'image, la publication — pour qu'un éditeur n'ait pas à recommencer chaque article à zéro.",
    "ChatGPT is excellent at writing one article on demand. delapresse is a pipeline that orchestrates sources, deduplication, image and publishing — so an editor does not start every article from scratch.",
  ),
  summaryChooseCompetitor: T(
    "Choisissez ChatGPT seul si vous voulez un copilote conversationnel, que vous gérez vous-même le sourcing, la publication et la cohérence éditoriale.",
    "Choose ChatGPT alone if you want a conversational copilot, and you manage sourcing, publishing and editorial consistency yourself.",
  ),
  summaryChooseDelapresse: T(
    "Choisissez delapresse si vous voulez que la chaîne de production tourne sans intervention — sources surveillées, déduplication, illustration, publication automatique, journal d'audit.",
    "Choose delapresse if you want the production chain to run hands-off — monitored sources, deduplication, illustration, automated publishing, audit log.",
  ),
  rows: [
    { id: "chat_interface", label: T("Interface conversationnelle libre", "Free-form chat interface", "Interfaz de chat libre", "واجهة دردشة حرة"),
      delapresse: N, competitor: Y() },
    { id: "model_choice", label: T("Choix du modèle (Claude, Gemini, etc.)", "Model choice (Claude, Gemini, etc.)", "Elección de modelo (Claude, Gemini, etc.)", "اختيار النموذج (Claude, Gemini, إلخ)"),
      delapresse: Y(), competitor: N },
    { id: "source_ingestion", label: T("Ingestion automatique de sources RSS", "Automatic RSS ingestion", "Ingesta automática de RSS", "استيراد تلقائي لـ RSS"),
      delapresse: Y(), competitor: N },
    { id: "dedup", label: T("Déduplication sémantique sur 90 jours", "Semantic deduplication over 90 days", "Deduplicación semántica durante 90 días", "إزالة التكرار الدلالي لمدة 90 يومًا"),
      delapresse: Y(), competitor: N },
    { id: "image", label: T("Image générée intégrée au flux", "Image generated in-flow", "Imagen generada en el flujo", "توليد الصورة أثناء التدفق"),
      delapresse: Y(), competitor: P(T("DALL·E dans ChatGPT", "DALL·E inside ChatGPT", "DALL·E dentro de ChatGPT", "DALL·E داخل ChatGPT")) },
    { id: "seo_meta", label: T("Meta + JSON-LD + canonical", "Meta + JSON-LD + canonical", "Meta + JSON-LD + canonical", "Meta + JSON-LD + canonical"),
      delapresse: Y(), competitor: P() },
    { id: "publish", label: T("Publication directe sur le site", "Direct publishing to the site", "Publicación directa en el sitio", "النشر المباشر إلى الموقع"),
      delapresse: Y(), competitor: N },
    { id: "scheduling", label: T("Cadence horaire ou temps réel", "Hourly or real-time cadence", "Frecuencia horaria o en tiempo real", "تكرار كل ساعة أو في الوقت الفعلي"),
      delapresse: Y(), competitor: N },
    { id: "approval", label: T("File d'approbation éditoriale", "Editorial approval queue", "Cola de aprobación editorial", "قائمة انتظار للموافقة التحريرية"),
      delapresse: Y(), competitor: N },
    { id: "audit_log", label: T("Journal d'audit (sources, modèle, coût)", "Audit log (sources, model, cost)", "Registro de auditoría (fuentes, modelo, coste)", "سجل التدقيق (المصادر، النموذج، التكلفة)"),
      delapresse: Y(), competitor: P() },
    { id: "team_seats", label: T("Sièges d'équipe avec rôles", "Team seats with roles", "Asientos de equipo con roles", "مقاعد فريق مع أدوار"),
      delapresse: Y(), competitor: TXT("Selon abonnement ChatGPT", "Depends on ChatGPT plan") },
    { id: "translation", label: T("Traduction systématique multi-langues", "Systematic multi-language translation", "Traducción sistemática multilingüe", "ترجمة منهجية متعددة اللغات"),
      delapresse: Y(), competitor: Y() },
    { id: "cms_native", label: T("Intégration CMS native", "Native CMS integration", "Integración nativa con CMS", "تكامل CMS أصلي"),
      delapresse: Y(), competitor: N },
    { id: "multi_site", label: T("Multi-sites depuis une console", "Multi-site from one console", "Multisitio desde una sola consola", "مواقع متعددة من لوحة تحكم واحدة"),
      delapresse: Y(), competitor: N },
    { id: "data_residency", label: T("Données traitées en UE garanties", "Guaranteed EU data residency", "Residencia de datos en la UE garantizada", "ضمان إقامة البيانات في الاتحاد الأوروبي"),
      delapresse: Y(), competitor: P() },
    { id: "training_optout", label: T("Opt-out entraînement par défaut", "Training opt-out by default", "Exclusión de entrenamiento por defecto", "الانسحاب من التدريب افتراضيًا"),
      delapresse: Y(), competitor: P() },
  ],
  useCaseFit: [
    { id: "experiment", audience: T("Découverte / brainstorming éditorial", "Discovery / editorial brainstorming", "Descubrimiento / lluvia de ideas editorial", "الاكتشاف / العصف الذهني التحريري"),
      delapresseVerdict: T("Surdimensionné", "Oversized", "Demasiado grande", "كبيرة الحجم"),
      competitorVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية") },
    { id: "solo", audience: T("Un éditeur solo, site quotidien", "A solo publisher, daily site", "Un editor individual, sitio diario", "ناشر منفرد، موقع يومي"),
      delapresseVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية"),
      competitorVerdict: T("Trop manuel", "Too manual", "Demasiado manual", "يدوي للغاية") },
    { id: "agency", audience: T("Une agence avec sites clients", "An agency with client sites", "Una agencia con sitios de clientes", "وكالة ذات مواقع عملاء"),
      delapresseVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية"),
      competitorVerdict: T("Inadapté", "Not suitable", "No adecuado", "غير مناسب") },
    { id: "group", audience: T("Un groupe de presse multi-régional", "A multi-regional press group", "Un grupo de prensa multirregional", "مجموعة صحفية متعددة المناطق"),
      delapresseVerdict: T("Adapté (Entreprise)", "Fits (Enterprise plan)", "Adecuado (plan Enterprise)", "مناسب (خطة Enterprise)"),
      competitorVerdict: T("Inadapté", "Not suitable", "No adecuado", "غير مناسب") },
  ],
  pricingDelapresse: T(
    "$79 à $999 / mois, crédits inclus, publication automatique comprise.",
    "$79 to $999 / month, credits included, automated publishing bundled.",
  ),
  pricingCompetitor: T(
    "$20 / mois pour ChatGPT Plus ou $200 / mois pour ChatGPT Pro, plus le temps humain pour rédiger, illustrer, publier.",
    "$20 / month for ChatGPT Plus or $200 / month for ChatGPT Pro, plus the human time to write, illustrate, publish.",
  ),
  pricingNote: T(
    "Estimations indicatives au 15 mai 2026. Voir openai.com/chatgpt/pricing pour les forfaits actuels. Coût humain à intégrer pour le sourcing et la publication manuelle.",
    "Indicative estimates as of 15 May 2026. See openai.com/chatgpt/pricing for current plans. Human cost for sourcing and manual publishing should be factored in.",
  ),
  migration: [
    { step: 1, title: T("Lister les prompts récurrents", "List recurring prompts", "Listar prompts recurrentes", "قائمة المطالبات المتكررة"),
      body: T("Identifiez les prompts ChatGPT que vous réutilisez. delapresse les transforme en automatisation déclenchée par source.", "Identify the ChatGPT prompts you reuse. delapresse turns them into source-triggered automations.", "Identifique los prompts de ChatGPT que reutiliza. delapresse los convierte en automatizaciones activadas por fuentes.", "حدد المطالبات التي تعيد استخدامها في ChatGPT. تقوم delapresse بتحويلها إلى أوتوماتيكيات مستندة إلى المصادر.") },
    { step: 2, title: T("Connecter vos sources", "Connect your sources", "Conecte sus fuentes", "ربط مصادرك"),
      body: T("RSS, sitemaps, API JSON. La déduplication évite que vous publiiez deux fois la même actualité.", "RSS feeds, sitemaps, JSON APIs. Deduplication stops you from publishing the same story twice.", "Fuentes RSS, sitemaps, APIs JSON. La deduplicación evita que publique la misma historia dos veces.", "تغذيات RSS، خرائط المواقع، وواجهات API JSON. تمنع إزالة التكرار نشر نفس القصة مرتين.") },
    { step: 3, title: T("Lancer la cadence", "Launch the cadence", "Iniciar la frecuencia", "بدء التكرار"),
      body: T("Choisissez quotidien ou horaire, modèle préféré, file d'approbation si besoin. Premier article publié sous l'heure.", "Pick daily or hourly, preferred model, approval queue if needed. First article published within the hour.", "Elija entre diario u horario, modelo preferido, cola de aprobación si es necesario. Primer artículo publicado en menos de una hora.", "اختر يوميًا أو كل ساعة، النموذج المفضل، قائمة الموافقة إذا لزم الأمر. أول مقال ينشر خلال الساعة.") },
  ],
  sourceUrls: [
    "https://chat.openai.com/",
    "https://openai.com/chatgpt/pricing",
    "https://openai.com/policies/business-terms",
  ],
};

// ─── Zapier / Make ───────────────────────────────────────────────────────────

const AUTOMATION: CompetitorDefinition = {
  slug: "zapier-make",
  competitorName: "Zapier / Make",
  homepageUrl: "https://zapier.com/",
  heroTitle: T(
    "delapresse vs Zapier ou Make : ce que les médias choisissent en 2026",
    "delapresse vs Zapier or Make: what newsrooms pick in 2026",
  ),
  heroLead: T(
    "Zapier et Make sont des automatiseurs généralistes très puissants. delapresse est un produit dédié à la production éditoriale — pipeline, modèles, déduplication, SEO — sans qu'il vous faille assembler chaque brique.",
    "Zapier and Make are powerful generic automators. delapresse is a product dedicated to editorial production — pipeline, models, deduplication, SEO — without you assembling every brick yourself.",
  ),
  summaryChooseCompetitor: T(
    "Choisissez Zapier ou Make si vous voulez assembler des automatisations sur-mesure entre des dizaines d'outils, et vous êtes à l'aise pour câbler vous-même.",
    "Choose Zapier or Make if you want to assemble custom automations across dozens of tools, and you are comfortable wiring it yourself.",
  ),
  summaryChooseDelapresse: T(
    "Choisissez delapresse si vous voulez un produit éditorial fini — sourcing, IA, image, SEO, publication — où le câblage est déjà fait et où le coût par article est lisible.",
    "Choose delapresse if you want a finished editorial product — sourcing, AI, image, SEO, publishing — where the wiring is already done and per-article cost is legible.",
  ),
  rows: [
    { id: "purpose", label: T("Outil dédié à la production éditoriale", "Tool dedicated to editorial production", "Herramienta dedicada a la producción editorial", "أداة مخصصة للإنتاج التحريري"),
      delapresse: Y(), competitor: N },
    { id: "wiring_required", label: T("Câblage manuel des étapes", "Manual step wiring", "Conexión manual de pasos", "ربط الخطوات يدويًا"),
      delapresse: N, competitor: Y() },
    { id: "rss_ingestion", label: T("Ingestion RSS avec déduplication", "RSS ingestion with deduplication", "Ingesta de RSS con desduplicación", "استيراد RSS مع إزالة التكرارات"),
      delapresse: Y(), competitor: P(T("RSS oui, déduplication à coder", "RSS yes, dedup must be coded", "RSS sí, la desduplicación debe programarse", "RSS نعم، لكن إزالة التكرارات تتطلب ترميزًا")) },
    { id: "model_billing", label: T("Crédits IA inclus", "AI credits included", "Créditos de IA incluidos", "رصيد AI مدمج"),
      delapresse: Y(), competitor: N },
    { id: "model_choice", label: T("Choix du modèle par tâche", "Per-task model choice", "Elección de modelo por tarea", "اختيار النموذج لكل مهمة"),
      delapresse: Y(), competitor: P() },
    { id: "image_native", label: T("Image générée native", "Native image generation", "Generación de imágenes nativa", "توليد الصور الأصلي"),
      delapresse: Y(), competitor: P() },
    { id: "seo_native", label: T("SEO complet (meta, canonical, schema)", "Full SEO (meta, canonical, schema)", "SEO completo (meta, canonical, schema)", "SEO الكامل (وصف تعريفي، canonical، schema)"),
      delapresse: Y(), competitor: N },
    { id: "approval_queue", label: T("File d'approbation éditoriale", "Editorial approval queue", "Cola de aprobación editorial", "قائمة انتظار للموافقة التحريرية"),
      delapresse: Y(), competitor: N },
    { id: "multi_site", label: T("Multi-sites natif", "Native multi-site", "Multisitio nativo", "دعم متعدد المواقع أصلي"),
      delapresse: Y(), competitor: N },
    { id: "cost_visibility", label: T("Coût par article visible", "Per-article cost visible", "Coste por artículo visible", "تكلفة المقال الظاهرة لكل مقال"),
      delapresse: Y(), competitor: P() },
    { id: "ops_count", label: T("Quota d'opérations / pas de quota", "Operations quota / no quota", "Cuota de operaciones / sin cuota", "حصة العمليات / بدون حصة"),
      delapresse: TXT("Crédits IA", "AI credits"),
      competitor: TXT("Opérations / tasks", "Operations / tasks") },
    { id: "audit_log", label: T("Journal d'audit complet", "Full audit log", "Registro de auditoría completo", "سجل تدقيق كامل"),
      delapresse: Y(), competitor: P() },
    { id: "team_roles", label: T("Rôles éditoriaux", "Editorial roles", "Roles editoriales", "أدوار تحريرية"),
      delapresse: Y(), competitor: P() },
    { id: "white_label", label: T("Marque blanche", "White-label", "White-label", "العلامة البيضاء"),
      delapresse: Y(), competitor: N },
    { id: "cms_native", label: T("CMS en sortie native", "Native CMS output", "Salida nativa a CMS", "إخراج أصلي لنظام إدارة المحتوى (CMS)"),
      delapresse: Y(), competitor: Y() },
    { id: "support", label: T("Support éditorial humain", "Human editorial support", "Soporte editorial humano", "دعم تحريري بشري"),
      delapresse: Y(), competitor: TXT("Support produit", "Product support") },
  ],
  useCaseFit: [
    { id: "diy", audience: T("Bricoleur / cas d'usage mixte non-éditorial", "DIY / mixed non-editorial use case", "Autogestión / uso mixto no editorial", "استخدام ذاتي / مختلط غير تحريري"),
      delapresseVerdict: T("Hors scope", "Out of scope", "Fuera de alcance", "خارج النطاق"),
      competitorVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية") },
    { id: "solo_editorial", audience: T("Éditeur solo qui veut du clé en main", "Solo publisher wanting turnkey", "Editor individual que busca una solución llave en mano", "ناشر فردي يريد حلًا جاهزًا"),
      delapresseVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية"),
      competitorVerdict: T("Trop technique", "Too technical", "Demasiado técnico", "فني للغاية") },
    { id: "agency", audience: T("Agence multi-sites éditoriale", "Editorial multi-site agency", "Agencia editorial multisitio", "وكالة تحريرية متعددة المواقع"),
      delapresseVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية"),
      competitorVerdict: T("Coût et complexité à câbler", "Cost + complexity to wire", "Coste + complejidad para conectar", "التكلفة + التعقيد في الربط") },
    { id: "group", audience: T("Groupe de presse régional", "Regional press group", "Grupo de prensa regional", "مجموعة صحافة إقليمية"),
      delapresseVerdict: T("Adapté", "A good fit", "Adecuado", "مناسب جيدًا"),
      competitorVerdict: T("Pas adapté à grande échelle", "Not suited at scale", "No apto a gran escala", "غير مناسب على نطاق واسع") },
  ],
  pricingDelapresse: T(
    "$79 à $999 / mois, crédits inclus, sans quota d'opérations.",
    "$79 to $999 / month, credits included, no operations quota.",
  ),
  pricingCompetitor: T(
    "$20 à $600 / mois selon le forfait Zapier ou Make, plus les coûts d'API IA, plus les outils tiers (RSS reader, image generator, etc.) à raccorder.",
    "$20 to $600 / month depending on Zapier or Make plan, plus AI API costs, plus third-party tools (RSS reader, image generator, etc.) to wire in.",
  ),
  pricingNote: T(
    "Estimations indicatives au 15 mai 2026. Zapier facture à l'opération, Make au pas (operation/scenario). Le coût total dépend du nombre d'articles et des outils raccordés.",
    "Indicative estimates as of 15 May 2026. Zapier charges per task, Make per operation/scenario. Total cost depends on article volume and connected tools.",
  ),
  migration: [
    { step: 1, title: T("Audit de votre scénario actuel", "Audit your current scenario", "Audite su escenario actual", "تدقيق سيناريوهاتك الحالية"),
      body: T("Listez vos étapes : source, IA, image, publication, notification. delapresse couvre ces cinq étapes nativement.", "List your steps: source, AI, image, publishing, notification. delapresse covers all five natively.", "Enumere sus pasos: fuente, IA, imagen, publicación, notificación. delapresse cubre los cinco de forma nativa.", "أدرج خطواتك: المصدر، AI، الصورة، النشر، الإشعار. delapresse يغطي جميع الخطوات الخمسة أصليًا.") },
    { step: 2, title: T("Reproduire la cadence", "Reproduce the cadence", "Reproducir la cadencia", "إعادة إنتاج الإيقاع"),
      body: T("Importez vos sources RSS, choisissez le modèle, la cadence. La file d'approbation prend la place de votre étape de relecture manuelle.", "Import your RSS sources, pick the model, set the cadence. The approval queue replaces your manual review step.", "Importe sus fuentes RSS, elija el modelo, establezca la cadencia. La cola de aprobación reemplaza su revisión manual.", "استورد مصادر RSS الخاصة بك، اختر النموذج، ضع الإيقاع. قائمة الموافقة تستبدل خطوة المراجعة اليدوية.") },
    { step: 3, title: T("Couper le scénario", "Turn off the scenario", "Desactivar el escenario", "إيقاف السيناريو"),
      body: T("Une fois delapresse en production, désactivez votre scénario Zapier ou Make pour éviter les doubles publications.", "Once delapresse is in production, disable your Zapier or Make scenario to avoid double publishing.", "Una vez que delapresse esté en producción, desactive su escenario de Zapier o Make para evitar duplicados.", "بمجرد أن يكون delapresse في الإنتاج، تعطيل سيناريو Zapier أو Make لتجنب النشر المزدوج.") },
  ],
  sourceUrls: [
    "https://zapier.com/pricing",
    "https://www.make.com/en/pricing",
  ],
};

// ─── Content agency ──────────────────────────────────────────────────────────

const AGENCY: CompetitorDefinition = {
  slug: "agence-redaction",
  competitorName: "Agence de rédaction",
  homepageUrl: "https://www.snj.fr/",
  heroTitle: T(
    "delapresse vs agence de rédaction : ce que les médias choisissent en 2026",
    "delapresse vs content agency: what newsrooms pick in 2026",
  ),
  heroLead: T(
    "Une agence de rédaction apporte un savoir-faire humain. delapresse apporte un débit et un coût marginal proche de zéro. Les deux peuvent coexister — la question est ce que vous voulez que chacun fasse.",
    "A content agency brings human craft. delapresse brings throughput and a near-zero marginal cost. The two can coexist — the question is what you want each to do.",
  ),
  summaryChooseCompetitor: T(
    "Choisissez une agence de rédaction si vous avez besoin d'enquêtes longues, d'interviews exclusives, d'éditoriaux signés — bref, de tout ce qu'un journaliste humain fait mieux qu'un modèle.",
    "Choose a content agency if you need long-form investigations, exclusive interviews, signed editorials — anything a human journalist does better than a model.",
  ),
  summaryChooseDelapresse: T(
    "Choisissez delapresse pour la production récurrente à fort volume : reprises de dépêches, brèves locales, articles à faible valeur d'enquête. Gardez l'agence pour les contenus à forte marge éditoriale.",
    "Choose delapresse for high-volume recurring production: wire rewrites, local briefs, low-investigation articles. Keep the agency for high editorial-margin content.",
  ),
  rows: [
    { id: "humans", label: T("Rédaction humaine signée", "Signed human writing", "Escritura firmada por humanos", "الكتابة الموقعة من قبل بشر"),
      delapresse: N, competitor: Y() },
    { id: "investigations", label: T("Enquêtes longues / interviews", "Long-form investigations / interviews", "Investigaciones / entrevistas de formato largo", "تحقيقات طويلة / مقابلات"),
      delapresse: N, competitor: Y() },
    { id: "volume", label: T("Production 100+ articles par mois par site", "100+ articles a month per site", "100+ artículos al mes por sitio", "100+ مقال شهريًا لكل موقع"),
      delapresse: Y(), competitor: P(T("Possible mais coûteux", "Possible but expensive", "Posible pero costoso", "ممكن لكن مكلف")) },
    { id: "marginal_cost", label: T("Coût marginal par article", "Marginal cost per article", "Coste marginal por artículo", "التكلفة الهامشية لكل مقال"),
      delapresse: TXT("1 à 5 crédits", "1 to 5 credits"),
      competitor: TXT("80 à 400 €", "€80 to €400") },
    { id: "speed", label: T("Délai de production", "Production lead time", "Tiempo de producción", "وقت الإنتاج"),
      delapresse: TXT("Minutes", "Minutes"),
      competitor: TXT("Jours à semaines", "Days to weeks") },
    { id: "language_coverage", label: T("Couverture multi-langues à coût constant", "Multi-language coverage at flat cost", "Cobertura multilingüe a coste fijo", "تغطية متعددة اللغات بتكلفة ثابتة"),
      delapresse: Y(), competitor: N },
    { id: "consistency", label: T("Constance du ton sur le volume", "Tone consistency at volume", "Consistencia de tono a gran volumen", "اتساق النبرة عند الحجم الكبير"),
      delapresse: Y(), competitor: P() },
    { id: "editorial_judgement", label: T("Jugement éditorial sur sujet sensible", "Editorial judgement on sensitive topic", "Juicio editorial en temas sensibles", "الحكم التحريري على المواضيع الحساسة"),
      delapresse: N, competitor: Y() },
    { id: "approval_queue", label: T("File d'approbation humaine", "Human approval queue", "Cola de aprobación humana", "قائمة الموافقة البشرية"),
      delapresse: Y(), competitor: TXT("Process interne", "Internal process") },
    { id: "compliance_cppap", label: T("Conformité CPPAP / aide presse", "CPPAP / press subsidy compliance", "Cumplimiento de CPPAP / subvenciones de prensa", "الامتثال لمتطلبات CPPAP / دعم الصحافة"),
      delapresse: P(T("Auditable", "Auditable", "Auditable", "قابل للتدقيق")),
      competitor: Y() },
    { id: "image", label: T("Photo et illustration", "Photo and illustration", "Fotografía e ilustración", "الصورة والتوضيح"),
      delapresse: Y(), competitor: TXT("Banques + iconographes", "Stock + image researchers") },
    { id: "ip_rights", label: T("Pleins droits d'exploitation", "Full commercial rights", "Derechos comerciales completos", "الحقوق التجارية الكاملة"),
      delapresse: Y(), competitor: TXT("Selon contrat", "Contract-dependent") },
    { id: "team_oversight", label: T("Pilotage centralisé multi-sites", "Centralised multi-site oversight", "Supervisión centralizada multisitio", "الإشراف المركزي متعدد المواقع"),
      delapresse: Y(), competitor: P() },
    { id: "scaling", label: T("Passage à l'échelle sans embauche", "Scale without hiring", "Escalar sin contratar", "التوسع دون توظيف"),
      delapresse: Y(), competitor: N },
    { id: "exclusivity", label: T("Contenu exclusif non recyclé", "Exclusive non-recycled content", "Contenido exclusivo no reciclado", "محتوى حصري غير معاد تدويره"),
      delapresse: TXT("Si sources internes", "If internal sources"),
      competitor: Y() },
  ],
  useCaseFit: [
    { id: "investigative", audience: T("Magazine d'enquêtes", "Investigative magazine", "Revista de investigación", "مجلة تحقيقات"),
      delapresseVerdict: T("Inadapté seul", "Unsuitable alone", "Inadecuado por sí solo", "غير مناسب وحده"),
      competitorVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية") },
    { id: "local_daily", audience: T("Quotidien local fort volume", "High-volume local daily", "Diario local de alto volumen", "صحيفة يومية محلية عالية الحجم"),
      delapresseVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية"),
      competitorVerdict: T("Coût prohibitif", "Prohibitive cost", "Coste prohibitivo", "تكلفة باهظة") },
    { id: "b2b_brand", audience: T("Marque B2B avec blog", "B2B brand with blog", "Marca B2B con blog", "علامة تجارية B2B مع مدونة"),
      delapresseVerdict: T("Cas d'usage idéal", "Ideal use case", "Caso de uso ideal", "حالة الاستخدام المثالية"),
      competitorVerdict: T("Possible, lent", "Possible, slow", "Posible, pero lento", "ممكن، لكن بطيء") },
    { id: "press_group", audience: T("Groupe de presse multi-territorial", "Multi-territory press group", "Grupo de prensa multiterritorio", "مجموعة صحفية متعددة الأقاليم"),
      delapresseVerdict: T("Adapté + agence complémentaire", "Fits + complementary agency", "Se adapta + complementa con agencia", "مناسب + مكمل لوكالة"),
      competitorVerdict: T("Adapté pour signatures", "Fits for signed pieces", "Adecuado para piezas firmadas", "مناسب للمقالات الموقعة") },
  ],
  pricingDelapresse: T(
    "$79 à $999 / mois pour des centaines à plusieurs milliers d'articles, indépendamment du volume.",
    "$79 to $999 / month for hundreds to thousands of articles, regardless of volume.",
  ),
  pricingCompetitor: T(
    "Forfait au mois de 1 500 à 12 000 € pour une rédaction externalisée, ou tarif à l'article entre 80 € et 400 € HT selon longueur et expertise.",
    "Monthly retainer of €1 500 to €12 000 for an outsourced newsroom, or per-article rate of €80 to €400 ex VAT depending on length and expertise.",
  ),
  pricingNote: T(
    "Fourchettes basées sur les grilles syndicales et marchés observés début 2026. Les enquêtes longues et les exclusivités sortent de ce cadre par nature.",
    "Ranges based on union scales and observed market rates in early 2026. Long-form investigations and exclusives fall outside this frame by nature.",
  ),
  migration: [
    { step: 1, title: T("Partager la production", "Split the production", "Dividir la producción", "تقسيم الإنتاج"),
      body: T("Identifier ce qui peut tourner en automatique — brèves, dépêches, brefs locaux — et ce qui reste humain — analyses, enquêtes, signatures.", "Identify what can run automatically — briefs, wires, local snippets — and what stays human — analyses, investigations, signatures.", "Identifique qué puede automatizarse — breves, cables, notas locales — y qué debe seguir siendo humano: análisis, investigaciones, firmas.", "حدد ما يمكن تشغيله تلقائيًا — موجزات، أسلاك أنباء، مقتطفات محلية — وما يبقى بشريًا — تحليلات، تحقيقات، مقالات موقعة.") },
    { step: 2, title: T("Câbler delapresse sur le volume", "Wire delapresse on volume", "Conecte delapresse para volumen", "ربط delapresse بالحجم"),
      body: T("Sources, modèle, cadence. La file d'approbation peut être tenue par votre rédaction en chef pour garder la main.", "Sources, model, cadence. The approval queue can be held by your editor-in-chief to keep control.", "Fuentes, modelo, frecuencia. La cola de aprobación puede ser gestionada por su redactor jefe para mantener el control.", "المصادر، النموذج، الإيقاع. يمكن أن تدار قائمة الموافقة من قبل رئيس التحرير للحفاظ على السيطرة.") },
    { step: 3, title: T("Recentrer l'agence", "Refocus the agency", "Reenfocar la agencia", "إعادة تركيز الوكالة"),
      body: T("Renégocier le forfait sur le contenu à forte valeur. Le débit est couvert par delapresse, l'agence se concentre sur la qualité signée.", "Renegotiate the retainer on high-value content. Throughput is covered by delapresse, the agency focuses on signed quality.", "Renegocie el contrato de retención para contenido de alto valor. El volumen lo cubre delapresse; la agencia se centra en calidad firmada.", "إعادة التفاوض على الأتعاب للمحتوى عالي القيمة. يتم تغطية الإنتاجية بواسطة delapresse، وتركز الوكالة على الجودة الموقعة.") },
  ],
  sourceUrls: [
    "https://www.snj.fr/",
    "https://www.cppap.fr/",
  ],
};

export const COMPETITORS: Record<CompetitorSlug, CompetitorDefinition> = {
  "chatgpt-seul": CHATGPT,
  "zapier-make": AUTOMATION,
  "agence-redaction": AGENCY,
};

export const COMPETITOR_SLUGS: CompetitorSlug[] = [
  "chatgpt-seul",
  "zapier-make",
  "agence-redaction",
];

export function locText(t: BilingualText, locale: Locale): string {
  if (locale === "fr") return t.fr;
  return t[locale] || t.en;
}
